'use client';

import { ReactNode } from 'react';

import { GridColDef } from '@mui/x-data-grid';
import { useTranslations } from 'next-intl';

import { RecapAsset } from '@/api/trading/types';
import { useRecap } from '@/api/trading/useRecap';
import { ApiPlatform } from '@/api/types';
import { useColumnsVisibilityModel, useRowClick } from '@/blocks/components/DataGrid';
import {
  getColDef24hChange,
  getColDefBuy,
  getColDefCta,
  getColDefMarket,
  getColDefPriceAndChange,
  getColDefSell,
  getColDefTrend,
} from '@/blocks/components/DataGrid/Asset/columnsDefenitions';
import { AssetGridRowModel, getAssetRowModel } from '@/blocks/components/DataGrid/Asset/rowModel';
import { getCfdAssetCategoryBySlug, getCfdHubUrl } from '@/data';

type Props = {
  category?: string;
  initialData: RecapAsset[] | null;
};

export const useCfdMarketsTable = (_props: Props) => {
  const { category, initialData } = { ..._props };
  const tr = useTranslations();
  const { markets, loading } = useRecap(ApiPlatform.DEVEX, initialData);
  const { openSignupOnRowClick } = useRowClick();
  const columnsVisibilityModel = useColumnsVisibilityModel({
    mobile: ['name', 'priceAndChange'],
    tablet: ['name', 'ask', 'bid'],
    desktop: ['name', 'ask', 'bid', 'change24h', 'trendChart', 'cta'],
  });

  const tabs: { label: ReactNode; value: RecapAsset['category'] | 'all' }[] = [
    {
      label: tr.rich('pages.cfd.all.sections.markets.tabs.all'),
      value: getCfdHubUrl(),
    },
    {
      label: tr.rich('pages.cfd.all.sections.markets.tabs.forex'),
      value: getCfdHubUrl('forex'),
    },
    {
      label: tr.rich('pages.cfd.all.sections.markets.tabs.indices'),
      value: getCfdHubUrl('indices'),
    },
    {
      label: tr.rich('pages.cfd.all.sections.markets.tabs.commodities'),
      value: getCfdHubUrl('commodities'),
    },
    {
      label: tr.rich('pages.cfd.all.sections.markets.tabs.stocks'),
      value: getCfdHubUrl('shares'),
    },
    {
      label: tr.rich('pages.cfd.all.sections.markets.tabs.crypto'),
      value: getCfdHubUrl('crypto'),
    },
  ];

  let activeTabIndex = 0;
  if (category) {
    const categoryIndex = tabs.findIndex((tab) => tab.value.includes(category));
    if (categoryIndex !== -1) {
      activeTabIndex = categoryIndex;
    }
  }

  const selectedTab = tabs[activeTabIndex].value;

  if (!markets) {
    return null;
  }

  const assetsByCategory = markets
    // .filter((asset) => asset.category !== 'stocks')
    .filter((asset) => (category ? asset.category === getCfdAssetCategoryBySlug(category) : true))
    .sort((a, b) => {
      if (!a.volWeekUsd) {
        return -1;
      }
      if (!b.volWeekUsd) {
        return 1;
      }
      return b.volWeekUsd - a.volWeekUsd;
    });

  const rows = assetsByCategory.map(getAssetRowModel);

  const columns: GridColDef<AssetGridRowModel>[] = [
    getColDefMarket(tr('pages.cfd.all.sections.markets.table.headers.asset')),
    getColDefPriceAndChange(tr('pages.cfd.all.sections.markets.table.headers.price')),
    getColDefBuy(tr('pages.cfd.all.sections.markets.table.headers.buy')),
    getColDefSell(tr('pages.cfd.all.sections.markets.table.headers.sell')),
    // getColDefSpread(tr('pages.cfd.all.sections.markets.table.headers.spread')),
    getColDef24hChange(tr('pages.cfd.all.sections.markets.table.headers.24hChangePercent')),
    getColDefTrend(tr('pages.cfd.all.sections.markets.table.headers.trend')),
    getColDefCta({
      text: tr('pages.cfd.all.sections.markets.table.rowCta') as string,
      enText: tr.rich('en.pages.cfd.all.sections.markets.table.rowCta') as string,
    }),
  ];

  return {
    tabs,
    selectedTab,
    loading,
    columns,
    rows,
    onRowClick: openSignupOnRowClick,
    columnsVisibilityModel,
  };
};
